/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, spawn, withAssign } from '@reatom/framework'
import { match } from 'ts-pattern'

import { AsyncStateN, pipe, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomPairInfoBulkSubscription } from '@root/entities/pair/atoms'
import {
  fetchUserPositionsRequestAction,
  positionsRegistry,
} from '@root/entities/position'
import { userPositionUpdateEvent } from '@root/entities/realtime'
import { deviceWalletIdsAtom } from '@root/features/secure-storage/atoms'
import {
  dataTableNoAccessError,
  reatomDataTable,
  TableFilter,
} from '@root/shared/ui-kit'

import { createPositionsTableColumns } from '../ui/positions-table-columns'

import type { UserPositionsCreatePayload } from '@blade/trade-api-client'
import type {
  AggregatedPositionModel,
  WalletPositionModel,
} from '@root/entities/position'
import type { WalletId } from '@root/entities/wallet'
import type { TableData } from '@root/shared/ui-kit'

export const positionTableCurrencyTypeAtom = atom<'native' | 'usd'>(
  'native',
  'positionTableCurrencyTypeAtom',
)

export const togglePositionTableCurrencyType = action((ctx) => {
  positionTableCurrencyTypeAtom(ctx, (current) =>
    current === 'native' ? 'usd' : 'native',
  )
}, 'togglePositionTableCurrencyType')

export type PositionTableColumnId =
  | 'currency'
  | 'bought'
  | 'sold'
  | 'pnl'
  | 'remaining'
  | 'sell'
  | 'lastTrade'
  | 'wallet'
  | 'visibility'

export const reatomPositionsTable = (args?: { hiddenFilter?: boolean }) => {
  const paramsAtom = atom((ctx) => {
    const walletIds = ctx.spy(deviceWalletIdsAtom)
    return { walletIds }
  }, 'paramsAtom')
  const pairInfoSubscriptionModel = reatomPairInfoBulkSubscription({
    name: 'positions-table',
  })

  const tableModel = reatomDataTable<
    AggregatedPositionModel | WalletPositionModel,
    PositionTableColumnId,
    { walletIds: Set<WalletId> },
    {
      onOpenWalletPositions?: (value: AggregatedPositionModel) => void
    }
  >({
    columns: createPositionsTableColumns({ withSellAll: true }),
    name: 'positions-table',
    paramsAtom,
    loadItems: async (
      ctx,
      {
        sorting,
        pagination: { limit, offset },
        filters,
        params: { walletIds },
      },
    ) => {
      const isLoggedIn = ctx.get(isLoggedInAtom)
      if (!isLoggedIn) {
        return Result.fail(dataTableNoAccessError())
      }
      if (walletIds.size === 0) {
        return Result.succeed({ data: [] })
      }

      const network = ctx.get(selectedNetworkAtom)
      const address = filters.currency
        ? TableFilter.tryExtractStringFilterValue(filters.currency)
        : undefined

      const sortBy = match(sorting?.sortColumn)
        .returnType<UserPositionsCreatePayload['sortBy'] | undefined>()
        .with('bought', () => 'boughtSize')
        .with('sold', () => 'soldSize')
        .with('remaining', () => 'remainingSizeUsd')
        .with('pnl', () => 'pnl')
        .with('lastTrade', () => 'lastTrade')
        .otherwise(() => 'lastTrade')

      const result = await fetchUserPositionsRequestAction(ctx, {
        limit,
        offset,
        network,
        walletIds: [...walletIds],
        address,
        sortBy,
        sortOrder: sorting?.sortDirection,
        hidden: args?.hiddenFilter,
      })

      return result.pipe(
        Result.map(
          (it): TableData<AggregatedPositionModel | WalletPositionModel> => ({
            data: it.data.map((it) =>
              positionsRegistry.resolveFromDto(ctx, it),
            ),
            total: it.meta.total,
          }),
        ),
      )
    },
    limit: 20,
    getId: (position) =>
      'id' in position ? position.id : position.pairAddress,
  })

  const visibilityReaction = reaction((ctx) => {
    if (args?.hiddenFilter === undefined) {
      return
    }

    const state = ctx.spy(tableModel)
    if (!AsyncStateN.isSuccess(state)) {
      return
    }

    const items = state.value.data

    const updatedItems = items.filter((it) => {
      const hidden = ctx.spy(it.hidden)
      return hidden === args.hiddenFilter
    })

    if (items.length === updatedItems.length) {
      return
    }

    tableModel.setState(
      ctx,
      AsyncStateN.success({
        ...state.value,
        data: updatedItems,
      }),
    )
  }, 'visibilityReaction')

  const positionPairsStateAtom = atom((ctx) => {
    const state = ctx.spy(tableModel)
    return pipe(
      state,
      AsyncStateN.map((value) => value.data.map((it) => it.pair)),
    )
  }, 'pairsStateAtom')

  const initializeAction = action((ctx) => {
    const unsubPairInfos = ctx.subscribe(
      positionPairsStateAtom,
      (pairsState) => {
        if (AsyncStateN.isError(pairsState)) {
          pairInfoSubscriptionModel.unsubscribeAll(ctx)
          return
        }

        if (AsyncStateN.isSuccess(pairsState)) {
          pairInfoSubscriptionModel.processPairs(ctx, pairsState.value)
          return
        }
      },
    )

    const unsubscribePositionUpdate = userPositionUpdateEvent.onCall(
      (ctx, update) => {
        const state = ctx.get(tableModel)
        if (!AsyncStateN.isSuccess(state)) {
          return
        }

        const aggregatedPosition = state.value.data.find(
          (it) =>
            it.pairAddress === update.pair &&
            it.network === Network.fromDto(update.network),
        )

        if (!aggregatedPosition) {
          tableModel.load(ctx)
          return
        }
      },
    )

    const unsubscribeVisibility = spawn(ctx, (ctx) => visibilityReaction(ctx))

    return () => {
      unsubPairInfos()
      unsubscribePositionUpdate()
      unsubscribeVisibility.unsubscribe()
    }
  }, 'initializeAction')

  return tableModel.pipe(
    withAssign(() => {
      return {
        initialize: initializeAction,
      }
    }),
  )
}

export type PositionsTableModel = ReturnType<typeof reatomPositionsTable>
